import React from 'react';
import { conversationWorkspaceStrings } from '../../i18n/conversationWorkspace';
import { useTheme } from '../../theme/ThemeProvider';
import { getConfig } from '../../env';

interface ConnectorChooserProps {
  onClose: () => void;
  // Set when this chooser was opened from the "+" tools menu, so there is a
  // real main-menu to return to -- renders a Back control alongside Close.
  // Left unset when reused from SettingsScreen's Integrations section
  // (no tools menu to go back to there).
  onBack?: () => void;
}

export type ConnectorState = 'CONNECT' | 'UNAVAILABLE';

// Figma node 23:156 (CW-04). Per spec, connector state must come from the
// server, never be hardcoded as CONNECTED. This app has no generic
// connector-status endpoint today (Gate 1 discovery), so nothing here is
// ever shown as CONNECTED -- only Google Calendar and GitHub have a real
// backend (GET /api/connectors/google/calendar/oauth/start and
// /api/connectors/github/oauth/start, both already shipping), so their
// "Connect" buttons perform a real OAuth redirect; every other row is
// UNAVAILABLE and disabled rather than faked. Also reused by
// SettingsScreen's Integrations section (SettingsProducts.dc.html) so
// both surfaces show the exact same real connector set.
export const CONNECTORS: { id: string; label: string; state: ConnectorState; startPath?: string }[] = [
  { id: 'google-drive', label: 'Google Drive', state: 'UNAVAILABLE' },
  { id: 'gmail', label: 'Gmail', state: 'UNAVAILABLE' },
  { id: 'google-calendar', label: 'Google Calendar', state: 'CONNECT', startPath: '/api/connectors/google/calendar/oauth/start' },
  { id: 'github', label: 'GitHub', state: 'CONNECT', startPath: '/api/connectors/github/oauth/start' },
  { id: 'figma', label: 'Figma', state: 'UNAVAILABLE' },
  { id: 'slack', label: 'Slack', state: 'UNAVAILABLE' },
  { id: 'onedrive', label: 'OneDrive', state: 'UNAVAILABLE' },
  { id: 'dropbox', label: 'Dropbox', state: 'UNAVAILABLE' },
];

export function ConnectorChooser({ onClose, onBack }: ConnectorChooserProps) {
  const { language } = useTheme();
  const t = (key: string) => conversationWorkspaceStrings[language]?.[key] ?? conversationWorkspaceStrings.en[key] ?? key;

  return (
    <div className="ozira-cw-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="ozira-cw-panel" role="dialog" aria-modal="true" aria-label={t('chooseConnector')} onClick={(e) => e.stopPropagation()}>
        <div className="ozira-cw-panel-head">
          {onBack ? (
            <button type="button" className="ozira-cw-panel-back" aria-label={t('connectorBack')} onClick={onBack}>
              ←
            </button>
          ) : null}
          <h3>{t('chooseConnector')}</h3>
        </div>
        <ul className="ozira-cw-connector-list">
          {CONNECTORS.map((connector) => (
            <li key={connector.id} className="ozira-cw-connector-row">
              <span className="ozira-cw-row-title">{connector.label}</span>
              {connector.state === 'CONNECT' ? (
                <button
                  type="button"
                  className="ozira-cw-badge ozira-cw-badge--action"
                  onClick={() => {
                    window.location.href = `${getConfig().apiBase}${connector.startPath}`;
                  }}
                >
                  {t('connectorConnect')}
                </button>
              ) : (
                <span className="ozira-cw-badge">{t('connectorUnavailable')}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
